import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCapability } from '../../../../hooks/use-capability';
import { usePresence } from '../../../../hooks/use-presence';
import { api } from '../../../../lib/api-client';
import { useAuthStore } from '../../../../stores/auth-store';
import {
  BulkPostponeModal,
  BulkReassignModal,
  PageHead,
  ResolvePendencyModal,
} from '../../components';
import {
  useBulkPostpone,
  useBulkReassign,
  useResolvePendency,
  useTeamHeatmap,
  useTeamMetrics,
  useTeamPendencies,
} from '../../hooks/use-team-pendencies';

import { FilterChip } from './FilterChip';
import { TeamInboxView } from './TeamInboxView';
import {
  adaptHeatmap,
  adaptPendencyCard,
  adaptTeamMetrics,
  daysToNewDueAt,
  filterCards,
  PENDENCY_CATEGORIES,
  PENDENCY_STATUS_LABELS,
  type TeamFilters,
} from './tela4-adapter';

import type { PendencyCardVM, SupervisorVariant } from '../../view-models';
import type React from 'react';

type View = 'operator' | 'category' | 'timeline' | 'heatmap';

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

function SoloEmpty(): React.JSX.Element {
  return (
    <>
      <PageHead title="Inbox do Time" />
      <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <h3 style={{ color: 'var(--fg-1)' }}>Solo não suporta visão de time</h3>
        <p>Use Pendências (suas próprias) para gerenciar sua carga.</p>
      </div>
    </>
  );
}

export function InboxTimePage(): React.JSX.Element {
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));
  const canRead = useCapability('pendency-team-read');
  // Salto de conversa do SUPERVISOR vai para o espelho da Tela 2 (a página do
  // atendente exige atribuição — 403 not-assigned; provado no gate ao vivo v3.274).
  const canSeeConversation = useCapability('coattendance-shadow-observer');
  const navigate = useNavigate();
  const [view, setView] = useState<View>('category');
  const [filters, setFilters] = useState<TeamFilters>({});
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [actionIds, setActionIds] = useState<string[]>([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [postponeOpen, setPostponeOpen] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [resolveCard, setResolveCard] = useState<PendencyCardVM | null>(null);

  const reassign = useBulkReassign();
  const postpone = useBulkPostpone();
  const resolveMut = useResolvePendency();

  const { data: presence } = usePresence();
  const list = useTeamPendencies();
  const metrics = useTeamMetrics();
  const heatmap = useTeamHeatmap();

  if (variant === 'solo' || !canRead) return <SoloEmpty />;

  // Presence carrega o nome real do User (Engine) — nunca cair em id cru.
  const nameFor = (userId: string): string =>
    presence.find((p) => p.userId === userId)?.name ?? '—';

  async function handleExport(): Promise<void> {
    setExporting(true);
    setExportError(false);
    try {
      const blob = await api.exportTeamPendenciesCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pendencias-time.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  }

  function toggleSelect(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleGroup(ids: string[], allSelected: boolean): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }

  function clearSelection(): void {
    setSelected(new Set());
  }

  async function confirmReassign(p: {
    targetUserId: string;
    reason: string;
    notifyOperators: boolean;
    notifyManager: boolean;
    notifyConversation: boolean;
  }): Promise<void> {
    try {
      const res = await reassign.mutateAsync({ pendencyIds: actionIds, ...p });
      setBulkMsg({
        tone: 'success',
        text: `${res.succeeded.length.toString()} pendência(s) reatribuída(s).`,
      });
      setReassignOpen(false);
      if (actionIds.length === selected.size && actionIds.every((id) => selected.has(id)))
        setSelected(new Set());
    } catch {
      setBulkMsg({ tone: 'error', text: 'Falha ao reatribuir.' });
    }
  }

  async function confirmPostpone(p: { days: number; reason: string }): Promise<void> {
    try {
      const res = await postpone.mutateAsync({
        pendencyIds: actionIds,
        newDueAt: daysToNewDueAt(p.days),
        reason: p.reason,
      });
      const text =
        res.blocked.length === 0
          ? `${res.succeeded.length.toString()} pendência(s) adiada(s).`
          : `${res.succeeded.length.toString()} adiada(s) · ${res.blocked.length.toString()} bloqueada(s) (limite de adiamentos atingido).`;
      setBulkMsg({ tone: 'success', text });
      setPostponeOpen(false);
      if (actionIds.length === selected.size && actionIds.every((id) => selected.has(id)))
        setSelected(new Set());
    } catch {
      setBulkMsg({ tone: 'error', text: 'Falha ao adiar.' });
    }
  }

  async function confirmResolve(p: { resolutionNote?: string }): Promise<void> {
    if (resolveCard === null) return;
    try {
      await resolveMut.mutateAsync(
        p.resolutionNote !== undefined
          ? { id: resolveCard.id, version: resolveCard.version, resolutionNote: p.resolutionNote }
          : { id: resolveCard.id, version: resolveCard.version },
      );
      setBulkMsg({ tone: 'success', text: 'Pendência resolvida.' });
      setResolveCard(null);
    } catch {
      setBulkMsg({ tone: 'error', text: 'Falha ao resolver.' });
    }
  }

  function onCardReassign(id: string): void {
    setActionIds([id]);
    setReassignOpen(true);
  }

  function onCardPostpone(id: string): void {
    setActionIds([id]);
    setPostponeOpen(true);
  }

  function onCardResolve(card: PendencyCardVM): void {
    setResolveCard(card);
  }

  const now = new Date();
  const cards = (list.data?.data ?? []).map((p) => adaptPendencyCard(p, nameFor, now));
  const visibleCards = filterCards(cards, filters);
  const overdueTotal = visibleCards.filter((c) => c.overdue).length;
  const metricsVM = metrics.data !== undefined ? adaptTeamMetrics(metrics.data, nameFor) : null;
  const heatmapVM = heatmap.data !== undefined ? adaptHeatmap(heatmap.data.data, nameFor) : [];
  const isEmpty = !list.isLoading && visibleCards.length === 0;

  const targets = Array.from(new Map(cards.map((c) => [c.ownerUserId, c.opName])).entries()).map(
    ([value, label]) => ({ value, label }),
  );

  const operatorOpts = Array.from(new Set(cards.map((c) => c.opName))).map((name) => ({
    value: name,
    label: name,
  }));

  const categoryOpts = Object.entries(PENDENCY_CATEGORIES).map(([value, c]) => ({
    value,
    label: c.label,
  }));

  const statusOpts = ['open', 'in_progress', 'resolved', 'cancelled', 'expired'].map((value) => ({
    value,
    label: PENDENCY_STATUS_LABELS[value] ?? value,
  }));

  const vencimentoOpts = [
    { value: 'overdue', label: 'Vencidas' },
    { value: 'today', label: 'Vence hoje' },
    { value: 'next_24h', label: 'Vence em 24h' },
    { value: 'next_7d', label: 'Próximos 7 dias' },
    { value: 'no_due', label: 'Sem prazo' },
  ];

  return (
    <>
      <PageHead
        title="Inbox do Time"
        subtitle={
          isEmpty
            ? 'Equipe em dia · nenhuma pendência aberta'
            : `${visibleCards.length.toString()} pendências da equipe · ${overdueTotal.toString()} ${overdueTotal === 1 ? 'vencida' : 'vencidas'}`
        }
        filters={
          <>
            <div className="ti-filters">
              <FilterChip
                label="Operador"
                options={operatorOpts}
                value={filters.operator}
                onChange={(v) => {
                  setFilters((f) => {
                    const next: TeamFilters = { ...f };
                    if (v !== undefined) next.operator = v;
                    else delete next.operator;
                    return next;
                  });
                }}
              />
              <FilterChip
                label="Categoria"
                options={categoryOpts}
                value={filters.category}
                onChange={(v) => {
                  setFilters((f) => {
                    const next: TeamFilters = { ...f };
                    if (v !== undefined) next.category = v;
                    else delete next.category;
                    return next;
                  });
                }}
              />
              <FilterChip
                label="Status"
                options={statusOpts}
                value={filters.status}
                onChange={(v) => {
                  setFilters((f) => {
                    const next: TeamFilters = { ...f };
                    if (v !== undefined) next.status = v;
                    else delete next.status;
                    return next;
                  });
                }}
              />
              <FilterChip
                label="Vencimento"
                options={vencimentoOpts}
                value={filters.dueStatus}
                onChange={(v) => {
                  setFilters((f) => {
                    const next: TeamFilters = { ...f };
                    if (v !== undefined) next.dueStatus = v;
                    else delete next.dueStatus;
                    return next;
                  });
                }}
              />
            </div>
            <div className="tab-row" role="tablist">
              {(['operator', 'category', 'timeline', 'heatmap'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="tab"
                  aria-selected={view === v}
                  className={'tab-row__btn' + (view === v ? ' tab-row__btn--active' : '')}
                  onClick={() => {
                    setView(v);
                  }}
                >
                  {v === 'operator'
                    ? 'Operador'
                    : v === 'category'
                      ? 'Categoria'
                      : v === 'timeline'
                        ? 'Timeline'
                        : 'Heatmap'}
                </button>
              ))}
            </div>
          </>
        }
        actions={
          <>
            <button
              type="button"
              className="btn-ghost-mini"
              disabled={selected.size === 0}
              onClick={() => {
                setActionIds(Array.from(selected));
                setReassignOpen(true);
              }}
            >
              Reatribuir em massa
            </button>
            <button
              type="button"
              className="btn-ghost-mini"
              disabled={selected.size === 0}
              onClick={() => {
                setActionIds(Array.from(selected));
                setPostponeOpen(true);
              }}
            >
              Adiar em massa
            </button>
            <button
              type="button"
              className="btn-ghost-mini"
              disabled={exporting}
              onClick={() => {
                void handleExport();
              }}
            >
              {exporting ? 'Exportando…' : 'Export CSV'}
            </button>
          </>
        }
      />

      {exportError && (
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">Falha ao exportar CSV.</span>
          </div>
        </div>
      )}

      {bulkMsg !== null && (
        <div
          className={'banner ' + (bulkMsg.tone === 'error' ? 'banner--danger' : 'banner--info')}
          role={bulkMsg.tone === 'error' ? 'alert' : 'status'}
        >
          <div className="banner__body">
            <span className="banner__title">{bulkMsg.text}</span>
          </div>
        </div>
      )}

      {list.isError && (
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">Falha ao carregar as pendências do time.</span>
          </div>
        </div>
      )}

      {!isEmpty && !list.isError && (
        <div className="tela4-body">
          {selected.size > 0 && (
            <div className="tweaks-hint">
              {selected.size.toString()} selecionada(s) ·{' '}
              <button
                type="button"
                className="btn-ghost-mini"
                onClick={() => {
                  clearSelection();
                }}
              >
                Limpar seleção
              </button>
            </div>
          )}

          {metricsVM !== null && (
            <div className="ti-metrics">
              <div className="ti-metric">
                <div className="ti-metric__label">Pendência mais antiga</div>
                <div className="ti-metric__value ti-metric__value--danger">
                  {metricsVM.oldestOverdue !== null
                    ? `vencida há ${metricsVM.oldestOverdue.hours.toString()}h`
                    : '—'}
                </div>
                <div className="ti-metric__sub">
                  {metricsVM.oldestOverdue !== null
                    ? `atribuída a ${metricsVM.oldestOverdue.op}`
                    : 'sem pendências vencidas'}
                </div>
              </div>
              <div className="ti-metric">
                <div className="ti-metric__label">Operador com mais vencidas</div>
                <div className="ti-metric__value">{metricsVM.bottleneckOp?.name ?? '—'}</div>
                <div className="ti-metric__sub">
                  {metricsVM.bottleneckOp !== null ? (
                    <b style={{ color: 'var(--danger-fg)' }}>
                      {metricsVM.bottleneckOp.overdue.toString()}{' '}
                      {metricsVM.bottleneckOp.overdue === 1 ? 'vencida' : 'vencidas'}
                    </b>
                  ) : (
                    'nenhum'
                  )}
                </div>
              </div>
              <div className="ti-metric">
                <div className="ti-metric__label">Categoria gargalo</div>
                <div className="ti-metric__value">{metricsVM.bottleneckCat?.label ?? '—'}</div>
                <div className="ti-metric__sub">
                  {metricsVM.bottleneckCat !== null ? (
                    <b style={{ color: 'var(--danger-fg)' }}>
                      {metricsVM.bottleneckCat.overdue.toString()}{' '}
                      {metricsVM.bottleneckCat.overdue === 1 ? 'vencida' : 'vencidas'}
                    </b>
                  ) : (
                    'nenhuma'
                  )}
                </div>
              </div>
            </div>
          )}

          <TeamInboxView
            cards={visibleCards}
            view={view}
            heatmap={heatmapVM}
            selected={selected}
            onToggleSelect={toggleSelect}
            onToggleGroup={toggleGroup}
            onReassign={onCardReassign}
            onPostpone={onCardPostpone}
            onResolve={onCardResolve}
            {...(canSeeConversation
              ? {
                  onOpenConversation: (id: string): void => {
                    navigate(`/supervisor/acompanhamento/${id}`);
                  },
                }
              : {})}
          />
        </div>
      )}

      {isEmpty && !list.isError && (
        <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
          <div style={{ fontSize: 48 }}>☕</div>
          <h3 style={{ color: 'var(--fg-1)' }}>Nenhuma pendência aberta no time.</h3>
          <p>Equipe em dia.</p>
        </div>
      )}

      <BulkReassignModal
        open={reassignOpen}
        count={actionIds.length}
        targets={targets}
        submitting={reassign.isPending}
        onClose={() => {
          setReassignOpen(false);
        }}
        onConfirm={(p) => {
          void confirmReassign(p);
        }}
      />
      <BulkPostponeModal
        open={postponeOpen}
        count={actionIds.length}
        submitting={postpone.isPending}
        onClose={() => {
          setPostponeOpen(false);
        }}
        onConfirm={(p) => {
          void confirmPostpone(p);
        }}
      />
      <ResolvePendencyModal
        open={resolveCard !== null}
        pendencyTitle={resolveCard?.title ?? ''}
        submitting={resolveMut.isPending}
        onClose={() => {
          setResolveCard(null);
        }}
        onConfirm={(p) => {
          void confirmResolve(p);
        }}
      />
    </>
  );
}
